import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Mail, Lock, Shield, User, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const AddEmployee = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'telecaller',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api('/users/register', {
        method: 'POST',
        body: {
          ...formData,
          tenantId: user?.tenantId // Ensure it uses the admin's tenantId if available
        }
      });

      if (response.ok) {
        alert("Employee registered successfully!");
        navigate('/employees');
      } else {
        const data = await response.json();
        setError(data.message || "Registration failed");
        if (data.code === 'LIMIT_REACHED') {
          setLimitReached(true);
        }
      }
    } catch (err) {
      setError("Connection error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title="Register New Employee">
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div className="card" style={{ padding: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ padding: '10px', background: 'var(--color-background-tertiary)', borderRadius: '10px' }}>
              <UserPlus size={24} color="var(--color-brand-primary)" />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700' }}>Create User Account</h2>
              <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>Add a new team member to the CRM</p>
            </div>
          </div>

          {error && (
            <div style={{ padding: '12px', background: '#fee2e2', color: '#ef4444', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' }}>
              {error}
              {limitReached && (
                <div style={{ marginTop: '10px' }}>
                  <button 
                    onClick={() => navigate('/admin/pricing')}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Upgrade Plan Now
                  </button>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <FormInput 
                label="Full Name" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                icon={<User size={16} />} 
                placeholder="John Doe" 
              />
              <FormInput 
                label="Email Address" 
                name="email" 
                type="email" 
                value={formData.email} 
                onChange={handleChange} 
                icon={<Mail size={16} />} 
                placeholder="john@example.com" 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <FormInput 
                label="Login Password" 
                name="password" 
                type="password" 
                value={formData.password} 
                onChange={handleChange} 
                icon={<Lock size={16} />} 
                placeholder="Minimum 6 characters" 
              />
              <FormInput 
                label="Phone Number (Optional)" 
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                icon={<Phone size={16} />} 
                placeholder="+91 XXXXX XXXXX" 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600' }}>Assign Role</label>
                <div style={{ position: 'relative' }}>
                  <Shield size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                  <select 
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 40px',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border-primary)',
                      fontSize: '14px',
                      background: 'white'
                    }}
                  >
                    <option value="telecaller">Telecaller</option>
                    <option value="executive">Sales Executive</option>
                    <option value="manager">Manager</option>
                    <option value="site-visit-agent">Site Visit Agent</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '12px', display: 'flex', gap: '12px' }}>
              <button 
                type="submit" 
                disabled={loading}
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: 'var(--color-brand-primary)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Registering...' : 'Create Account'}
              </button>
              <button 
                type="button" 
                onClick={() => navigate(-1)}
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: 'white', 
                  color: 'var(--color-text-secondary)', 
                  border: '1px solid var(--color-border-primary)', 
                  borderRadius: '8px', 
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

const FormInput = ({ label, icon, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <label style={{ fontSize: '13px', fontWeight: '600' }}>{label}</label>
    <div style={{ position: 'relative' }}>
      {icon && <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }}>{icon}</div>}
      <input 
        {...props}
        style={{
          width: '100%',
          padding: icon ? '10px 12px 10px 40px' : '10px 12px',
          borderRadius: '8px',
          border: '1px solid var(--color-border-primary)',
          fontSize: '14px',
          boxSizing: 'border-box'
        }}
      />
    </div>
  </div>
);

export default AddEmployee;
